import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "../../Styles/yellowcalfcss/IncomeType.css";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

export function IncomeType() {
  /* ---------------- STATE ---------------- */
  const [recordsList, setRecordsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 10;
  const [hasNextPage, setHasNextPage] = useState(false);

  const [nameSearch, setNameSearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [dateSearch, setDateSearch] = useState("");

  const [displayModal, setDisplayModal] = useState(false);
  const [currentEditingRecord, setCurrentEditingRecord] = useState(null);

  // ✅ Added formIncomeType state for the dropdown
  const [formIncomeType, setFormIncomeType] = useState("");
  const [formTypeName, setFormTypeName] = useState("");
  const [formAccountName, setFormAccountName] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- FETCH LIST ---------------- */
  const fetchIncomeTypes = async (pageNo = page) => {
    setLoading(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      page: pageNo,
      limit,
      name_search: nameSearch,
      account_name_search: accountSearch,
      date_search: dateSearch,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.incomeTypeList,
        JSON.stringify(body)
      );

      if (res?.result) {
        setRecordsList(res.data || []);
        setHasNextPage(res.next_page === true);
      } else {
        setRecordsList([]);
        toast.error(res?.message || "Failed to load data");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- DEBOUNCED FILTER ---------------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchIncomeTypes(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [nameSearch, accountSearch, dateSearch]);

  useEffect(() => {
    fetchIncomeTypes(page);
  }, [page]);

  /* ---------------- OPEN MODAL HELPERS ---------------- */
  const openCreateModal = () => {
    setCurrentEditingRecord(null);
    setFormIncomeType("");       // ✅ reset dropdown
    setFormTypeName("");
    setFormAccountName("");
    setFormErrors({});
    setDisplayModal(true);
  };

  const openEditModal = (record) => {
    setCurrentEditingRecord(record);
    setFormIncomeType(record.income_type_category || ""); // ✅ pre-fill dropdown (use correct field name from your API)
    setFormTypeName(record.income_type_name);
    setFormAccountName(record.income_type_account_name);
    setFormErrors({});
    setDisplayModal(true);
  };

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const errors = {};
    if (!formIncomeType.trim()) errors.incomeType = "Please select an income type";
    if (!formTypeName.trim()) errors.type = "Type name required";
    if (!formAccountName.trim()) errors.account = "Account name required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ---------------- CREATE / UPDATE ---------------- */
  const handleSaveRecord = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      income_type_category: formIncomeType, // ✅ included in payload
      type_name: formTypeName,
      account_name: formAccountName,
      ...(currentEditingRecord && { id: currentEditingRecord.income_type_id }),
    };

    const api = currentEditingRecord
      ? ApiConfig.incomeTypeUpdate
      : ApiConfig.incomeTypeCreate;

    try {
      const res = await simplePostCall(api, JSON.stringify(body));
      if (res?.result) {
        toast.success(res.message);
        setDisplayModal(false);
        fetchIncomeTypes();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDeleteIncome = async () => {
    setIsDeleting(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: deleteId,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.incomeTypeDelete,
        JSON.stringify(body)
      );

      if (res?.result) {
        toast.success(res.message);
        fetchIncomeTypes();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="income-type-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="special-header panel-white-all">
        <h4 className="page-title">Income Type</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary" onClick={openCreateModal}>
            Create Income Type
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* Filters */}
        <div className="yc-list-filter">
          <Form.Control
            placeholder="Search by Name"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
          />
          <Form.Control
            placeholder="Search by Account"
            value={accountSearch}
            onChange={(e) => setAccountSearch(e.target.value)}
          />
          <Form.Control
            type="date"
            value={dateSearch || new Date().toISOString().split("T")[0]}
            onChange={(e) => setDateSearch(e.target.value)}
          />
        </div>

        {/* Column Header */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-6">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Name</div>
            <div className="yc-list-cell">Created Date</div>
            <div className="yc-list-cell">Account Name</div>
            <div className="yc-list-cell">Type</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "20vh" }}>
            <Spinner />
          </div>
        ) : recordsList.length === 0 ? (
          <div className="yc-empty-state">No data found</div>
        ) : (
          <div className="yc-list-row-list">
            {recordsList.map((record, index) => (
              <div key={record.income_type_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-6">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">{record.income_type_name}</div>
                  <div className="yc-list-cell">{record.income_type_created_date}</div>
                  <div className="yc-list-cell">{record.income_type_account_name}</div>
                  <div className="yc-list-cell">{record.income_type_category}</div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      title="Edit"
                      onClick={() => openEditModal(record)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      title="Delete"
                      onClick={() => {
                        setDeleteId(record.income_type_id);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        <div className="d-flex align-items-center gap-2 mt-3">
          <Button className="btn btn-primary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <span className="text-muted">Page {page}</span>
          <Button className="btn btn-primary" disabled={!hasNextPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      <Modal show={displayModal} onHide={() => setDisplayModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentEditingRecord ? "Edit Income Type" : "Create Income Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* ✅ Fully controlled dropdown with value, onChange, and validation */}
            <Form.Group className="mb-3">
              <Form.Label>Income Type</Form.Label>
              <Form.Select
                value={formIncomeType}
                isInvalid={!!formErrors.incomeType}
                onChange={(e) => setFormIncomeType(e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="service">Service</option>
                <option value="stopack">Stopack</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.incomeType}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type Name</Form.Label>
              <Form.Control
                value={formTypeName}
                isInvalid={!!formErrors.type}
                onChange={(e) => setFormTypeName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.type}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Account Name</Form.Label>
              <Form.Control
                value={formAccountName}
                isInvalid={!!formErrors.account}
                onChange={(e) => setFormAccountName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.account}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDisplayModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveRecord} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRM */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Income"
        message="Are you sure you want to delete this income record?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteIncome}
        isLoading={isDeleting}
      />
    </div>
  );
}